import React, { useContext, useEffect, useState, useMemo } from "react";
import Product from "./Product";
import { ProductContext } from "../context";
import { ThemeContext } from "./context/ThemeContexts";
import { AgeVerificationContext } from "../context/AgeVerificationContext";
import PurchaseSteps from "./PurchaseSteps";
import BottomInfoSection from "./BottomInfoSection";
import { useLocation } from "react-router-dom/cjs/react-router-dom";
import {
  AiOutlineSearch,
} from "react-icons/ai";
import { BsCaretDownFill, BsCaretUpFill } from "react-icons/bs";
import ImageCarousel from "./ImageCarousel";
import { ReactComponent as FullPackPurchaseIcon } from "../assets/productTabs/full-pack-purchase-button.svg";
import { ReactComponent as SinglePurchaseIcon } from "../assets/productTabs/single-purchse-button.svg";
import { ReactComponent as PreOrderIcon } from "../assets/productTabs/pre-order-button.svg";
import { ReactComponent as PurchaseStepsIcon } from "../assets/productTabs/purchase-steps-button.svg";

function ProductList() {
  const { isVerified, verifyAge } = useContext(AgeVerificationContext);
  const [activeTab, setActiveTab] = useState(""); // Default active tab (empty for showing all)
  const [activeTabBar, setActiveTabBar] = useState("整盒購買"); // Default tab
  const [sortOrder, setSortOrder] = useState("none"); // Default sort order (none for random)

  const location = useLocation(); // Tracks the current route location
  const queryParams = new URLSearchParams(location.search);
  const initialTab = queryParams.get("tab") || "整盒購買"; // Default to "整盒購買" if no tab provided

  // Use context hooks instead of consumers
  const value = useContext(ProductContext);
  const { theme } = useContext(ThemeContext);
  
  const { fetchProducts } = value;

  useEffect(() => {
    fetchProducts();
  }, [location, fetchProducts]);

  useEffect(() => {
    setActiveTabBar(initialTab); // Update tab state when location changes
  }, [location, initialTab]);

  const tabMapping = {
    整盒購買: "在售整盒",
    限時優惠: "在售單支",
    陳年老茄: "售罄可預訂",
    購買流程: "購買及保障",
  };

  const toggleSortOrder = () => {
    setSortOrder((prevOrder) =>
      prevOrder === "ascending"
        ? "descending"
        : prevOrder === "descending"
        ? "none"
        : "ascending"
    );
  };

  // Memoize brands calculation
  const brands = useMemo(
    () => [...new Set(value.products.map((product) => product.brand))],
    [value.products]
  );

  // Memoize filtered and sorted products
  const sortedProducts = useMemo(() => {
    // Filter products
    let filtered = value.products;
    
    if (activeTabBar && activeTabBar !== "整盒購買") {
      filtered = filtered.filter((product) => product.category === activeTabBar);
    } else if (activeTab && activeTabBar) {
      filtered = filtered.filter(
        (product) =>
          product.brand === activeTab && product.category === activeTabBar
      );
    } else if (activeTab) {
      filtered = filtered.filter((product) => product.brand === activeTab);
    } else if (activeTabBar) {
      filtered = filtered.filter((product) => product.category === activeTabBar);
    }

    // Sort products
    if (sortOrder === "none") {
      // When showing 全部 under 整盒購買, sort by brand name
      if (activeTabBar === "整盒購買" && activeTab === "") {
        return [...filtered].sort((a, b) => {
          if (a.brand > b.brand) return -1;
          if (a.brand < b.brand) return 1;
          return 0;
        });
      }
      return filtered; // No sorting otherwise
    }

    return [...filtered].sort((a, b) => {
      if (sortOrder === "ascending") {
        return a.price - b.price;
      } else {
        return b.price - a.price;
      }
    });
  }, [value.products, activeTab, activeTabBar, sortOrder]);

  const tabIcons = {
    整盒購買: FullPackPurchaseIcon,
    限時優惠: SinglePurchaseIcon,
    陳年老茄: PreOrderIcon,
    購買流程: PurchaseStepsIcon,
  };

  return (
    <div style={{ backgroundColor: "#f8f9fa" }}>
      {/* Age Verification Modal */}
      {!isVerified && (
        <div className="modal-background">
          <div className="modal-content">
            <h1 className="brand-name text-xl font-extrabold mb-4 text-blue-400">
              Garturbo
            </h1>
            <h2>請問您是否已滿18歲</h2>
            <button
              onClick={verifyAge}
              className="btn btn-primary"
              style={{ color: "black" }}
            >
              確認
            </button>
            <p style={{ marginTop: 20 }}>
              <strong>[嚴正聲明]</strong>
            </p>
            <p>
              • 客戶必須依所在國家或地區的海關要求如實報關及繳付稅款
            </p>
            <p>
              •
              為維護客戶於茄多堡Garturbo的購買安全與合法權益，客戶在瀏覽網站及購買前，應先了解網站下方列明的使用條款
            </p>
            <hr
              style={{
                border: "none",
                borderTop: "1px solid #5A5A5A",
                width: "100%",
                margin: "20px 0",
              }}
            />
            <p style={{ marginTop: 20 }}>
              <strong>忠告：吸煙危害健康</strong>
            </p>
            <p>• 依香港煙草法例規定，本網站僅年滿18歲瀏覽</p>
            <p>
              •
              所有交易於香港特別行政區進行，服務條款及程序均受香港特別行政區法律管轄
            </p>
          </div>
        </div>
      )}

      {/* Normal Tab Bar */}
      {isVerified && (
        <>
          <ImageCarousel />
          {/* 🔍 Search bar */}
          <div
            style={{
              padding: "1rem 1rem 0.5rem 1rem",
              backgroundColor: "#f8f9fa",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                border: "2px solid #b68e5b",
                borderRadius: "50px",
                padding: "0.5rem 1rem",
                backgroundColor: "#fff",
              }}
            >
              <AiOutlineSearch
                size={20}
                style={{ marginRight: "0.5rem" }}
              />
              <input
                type="text"
                onChange={(e) => value.filterProducts(e.target.value)}
                style={{
                  flex: 1,
                  border: "none",
                  outline: "none",
                  fontSize: "1rem",
                  backgroundColor: "transparent",
                }}
              />
            </div>
          </div>
          <div
            className="normal-tab-bar"
            style={{
              display: "flex",
              justifyContent: "space-around",
              padding: "1rem 0",
            }}
          >
            {Object.keys(tabMapping).map((tab) => {
              const isActive = activeTabBar === tab;
              const IconComponent = tabIcons[tab];

              return (
                <div
                  key={tab}
                  onClick={() => setActiveTabBar(tab)}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    padding: "0.75rem 0.5rem",
                    borderRadius: "12px",
                    backgroundColor: isActive
                      ? "#e4e6f0"
                      : "transparent", // sampled from screenshot
                    transition: "all 0.2s ease",
                  }}
                >
                  <IconComponent
                    style={{
                      width: "18vw", // scales with screen
                      maxWidth: "75px", // upper limit
                      height: "auto",
                      opacity: isActive ? 1 : 0.6,
                    }}
                  />
                  <span
                    style={{
                      fontSize: "0.75rem",
                      fontWeight: "normal",
                      color: "#333", // unified text color
                      marginTop: "0.25rem",
                    }}
                  >
                    {tabMapping[tab]}
                  </span>
                </div>
              );
            })}
          </div>
        </>
      )}
      {/* Sort Toggle */}
      {activeTabBar !== "購買流程" && (
        <div
          className="sort-toggle"
          style={{
            display: "flex",
            justifyContent: "flex-end", // Align to the right
            marginRight: "1rem",
          }}
        >
          <button
            onClick={toggleSortOrder}
            style={{
              backgroundColor:
                sortOrder !== "none" ? "#b68e5b" : "#f8f9fa", // Keep a neutral background
              border: "1px solid #ddd",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "0.7rem",
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
              gap: "0.2rem",
              padding: "0.2rem",
              color: sortOrder !== "none" ? "#fff" : "#000", // Highlight text color
            }}
          >
            價格排序
            {sortOrder === "ascending" || sortOrder === "none" ? (
              <BsCaretUpFill />
            ) : sortOrder === "descending" ? (
              <BsCaretDownFill />
            ) : null}
          </button>
        </div>
      )}

      {activeTabBar === "購買流程" ? (
        <>
          <PurchaseSteps />
        </>
      ) : (
        <div className="main-layout">
          {/* Vertical Tabs */}
          {activeTabBar === "整盒購買" && (
            <div className="vertical-tabs">
              <button
                className={`vertical-tab ${
                  activeTab === "" ? "active" : ""
                }`}
                style={{
                  color: theme ? "#FFFFFF" : "#000000",
                }}
                onClick={() => setActiveTab("")}
              >
                全部
              </button>
              {brands
                .filter((brand) =>
                  value.products.some(
                    (product) =>
                      product.brand === brand &&
                      product.category === "整盒購買"
                  )
                )
                .map((brand) => (
                  <button
                    key={brand}
                    className={`vertical-tab ${
                      activeTab === brand ? "active" : ""
                    }`}
                    style={{
                      color: theme ? "#FFFFFF" : "#000000",
                    }}
                    onClick={() => setActiveTab(brand)}
                  >
                    {brand}
                  </button>
                ))}
            </div>
          )}

          {/* Product List */}
          <div className="product-list">
            {sortedProducts.length > 0 ? (
              <div className="row">
                {sortedProducts.map((product) => (
                  <Product key={product.id} product={product} />
                ))}
              </div>
            ) : (
              <div className="no-results">
                <p>抱歉，没有相关产品</p>
              </div>
            )}
          </div>
        </div>
      )}

      <hr
        style={{
          border: "none",
          marginLeft: "12px",
          marginRight: "12px",
          height: activeTabBar === "購買流程" ? "0rem" : "10rem",
          borderBottom: "2px solid #ddd",
          backgroundColor: "#f8f9fa",
          marginBottom: "-0.01rem",
        }}
      />
      {/* Info Section */}
      <div className="info-section" style={{}}>
        <BottomInfoSection />
      </div>
    </div>
  );
}

export default ProductList;
